import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Unlink } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  WeightGlyph,
} from '@/components/ui';
import {
  getWeightStatus,
  WEIGHT,
  WEIGHT_OPTIONS,
  type WeightStatus,
} from '@/logic/weights';
import type { PointPair } from '@/types';
import { WEIGHT_SHORTCUTS } from './mapDisplay';

export interface PointMenuState {
  pairId: number;
  /** Viewport position of the pointer (or the focused point). */
  x: number;
  y: number;
  /** Element that receives focus again when the menu closes. */
  returnFocus: Element | null;
}

interface PointContextMenuProps {
  state: PointMenuState;
  pair: PointPair;
  onClose: () => void;
  onSetWeight: (id: number, weight: number) => void;
  onEditPair?: (id: number) => void;
  onRemovePair?: (id: number) => void;
}

/**
 * Point options at the pointer, built on the design-system DropdownMenu
 * (role="menu", arrow keys, typeahead, Escape). Render it only while open:
 * the menu and its document listeners mount with it and go away on close.
 * The first item takes focus on open; focus returns to the point on close.
 */
export const PointContextMenu = ({
  state,
  pair,
  onClose,
  onSetWeight,
  onEditPair,
  onRemovePair,
}: PointContextMenuProps) => {
  const restoreFocus = useRef(true);

  // Focus the first item as it mounts. Radix then sees focus inside the
  // menu and keeps it there instead of focusing the menu container.
  const focusOnMount = useCallback((item: HTMLDivElement | null) => {
    if (!item) return;
    restoreFocus.current = true;
    item.focus({ preventScroll: true });
  }, []);

  return createPortal(
    <DropdownMenu
      modal={false}
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden="true"
          tabIndex={-1}
          className="pointer-events-none fixed size-px opacity-0"
          style={{ left: state.x, top: state.y }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={2}
        className="min-w-[13rem]"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const target = state.returnFocus;
          if (
            restoreFocus.current &&
            target instanceof Element &&
            target.isConnected &&
            'focus' in target
          ) {
            (target as HTMLElement | SVGElement).focus();
          }
        }}
      >
        <DropdownMenuLabel className="normal-case tracking-normal">
          <span className="font-mono text-xs text-fg">
            Record {pair.sys1Point} → Field {pair.sys2Point}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuLabel>Weight</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={getWeightStatus(pair.weight)}
          onValueChange={(status) =>
            onSetWeight(pair.id, WEIGHT[status as WeightStatus])
          }
        >
          {WEIGHT_OPTIONS.map((option, index) => (
            <DropdownMenuRadioItem
              key={option.status}
              ref={index === 0 ? focusOnMount : undefined}
              value={option.status}
              icon={<WeightGlyph status={option.status} />}
            >
              <span className="flex-1">{option.label}</span>
              <DropdownMenuShortcut>
                {WEIGHT_SHORTCUTS[option.status]}
              </DropdownMenuShortcut>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {(onEditPair || onRemovePair) && <DropdownMenuSeparator />}
        {onEditPair && (
          <DropdownMenuItem
            icon={<Pencil />}
            onSelect={() => {
              // The pair dialog takes focus; do not pull it back to the map.
              restoreFocus.current = false;
              onEditPair(pair.id);
            }}
          >
            Edit coordinates or match
          </DropdownMenuItem>
        )}
        {onRemovePair && (
          <DropdownMenuItem
            icon={<Unlink />}
            onSelect={() => onRemovePair(pair.id)}
          >
            Unlink pair
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>,
    document.body
  );
};
